import { ArrowRight, Sparkles, TrendingUp, Target, Zap } from "lucide-react";
import { HERO } from "@/constants/testIds";

export default function Hero() {
  return (
    <section id="home" className="relative overflow-hidden bg-white">
      <div className="absolute inset-0 bg-grid opacity-70 pointer-events-none" />
      <div className="absolute inset-0 bg-radial-fade pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold uppercase tracking-widest">
            <Sparkles className="w-3.5 h-3.5" /> The AI Growth Pilot
          </div>
          <h1
            data-testid={HERO.headline}
            className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight text-slate-900"
          >
            <span className="text-blue-600">Websites</span>, <span className="text-blue-600">SEO</span>,{" "}
            <span className="text-blue-600">Paid Ads</span> &amp;{" "}
            <span className="text-blue-600">AI Automation</span> for businesses that want more leads.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl">
            We help businesses grow traffic, generate qualified enquiries and improve conversions —
            with modern websites, SEO, high-performing paid ads and AI-powered marketing automation.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="#free-audit-form"
              data-testid={HERO.primaryCta}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 text-white hover:bg-blue-700 font-semibold py-4 px-7 rounded-full transition-all shadow-lg shadow-blue-600/20 active:scale-95"
            >
              Book Free Growth Audit <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#free-audit-form"
              data-testid={HERO.secondaryCta}
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 font-semibold py-4 px-7 rounded-full transition-all active:scale-95"
            >
              Get Free Marketing Review
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 max-w-lg gap-6">
            {[
              { k: "6+", v: "Years Experience" },
              { k: "60K+", v: "Keywords Ranked" },
              { k: "25+", v: "SEO Pros Led" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-2xl md:text-3xl font-bold text-slate-900">{s.k}</div>
                <div className="text-xs md:text-sm text-slate-500 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-2xl shadow-blue-600/10">
              <img
                src="https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJsdWUlMjBkaWdpdGFsJTIwbWFya2V0aW5nfGVufDB8fHx8MTc4MzM0MzU3OHww&ixlib=rb-4.1.0&q=85"
                alt="Digital growth analytics"
                loading="lazy"
                className="w-full h-[420px] md:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 via-transparent to-transparent" />
            </div>

            <div className="absolute -left-6 top-10 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 w-56 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 grid place-items-center">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Organic Traffic</div>
                  <div className="font-display font-bold text-slate-900">+200%</div>
                </div>
              </div>
            </div>

            <div className="absolute -right-4 bottom-14 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 w-60 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 grid place-items-center">
                  <Target className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500">Domain Authority</div>
                  <div className="font-display font-bold text-slate-900">8 → 41</div>
                </div>
              </div>
            </div>

            <div className="absolute right-8 -top-6 bg-slate-900 text-white rounded-2xl shadow-xl p-4 w-56 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-600/20 text-blue-300 grid place-items-center">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-blue-300">AI Automation</div>
                  <div className="font-display font-semibold text-sm">Always-on funnels</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
